import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import React from 'react';
import { Spin } from 'antd';
import MainLayout from '@/components/layout/MainLayout';
import AuthGuard from '@/components/auth/AuthGuard';

// 加载指示器
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      minHeight: 400,
    }}
  >
    <Spin size="large" />
    <div style={{ marginTop: 16 }}>加载中...</div>
  </div>
);

// 页面懒加载
const Home = lazy(() => import('@/pages/Home'));
const Workbench = lazy(() => import('@/pages/Workbench'));
const AssessmentUpload = lazy(() => import('@/pages/Assessment/Upload'));
const AssessmentHistory = lazy(() => import('@/pages/Assessment/History'));
const ScheduleGenerate = lazy(() => import('@/pages/Schedule/Generate'));
const GroupSchedule = lazy(() => import('@/pages/Schedule/GroupSchedule'));
const RosterManagement = lazy(() => import('@/pages/Schedule/RosterManagement'));
const TimelinePdfExport = lazy(() => import('@/pages/Schedule/TimelinePdfExport'));
const CareAdjust = lazy(() => import('@/pages/Schedule/CareAdjust'));
const CaregiverList = lazy(() => import('@/pages/Caregiver/List'));
const GroupList = lazy(() => import('@/pages/GroupManagement/index'));
const ElderList = lazy(() => import('@/pages/Elder/List'));
const CareElderManagement = lazy(() => import('@/pages/Elder/CareElderManagement'));
const SkillList = lazy(() => import('@/pages/Skill/List'));
const CaregiverSkills = lazy(() => import('@/pages/Caregiver/SkillsGenerator'));
const ElderNeeds = lazy(() => import('@/pages/DataGenerator/ElderNeeds'));
const MatchingScenario = lazy(() => import('@/pages/DataGenerator/MatchingScenario'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const NationalDashboard = lazy(() => import('@/pages/Dashboard/NationalDashboard/index'));
const Timeline = lazy(() => import('@/pages/Timeline'));
const OCR = lazy(() => import('@/pages/OCR'));
const SOPImport = lazy(() => import('@/pages/SOP/Import'));
const SOPManagement = lazy(() => import('@/pages/SOP/Management'));
const NursingActions = lazy(() => import('@/pages/SOP/NursingActions'));
const ShiftConfig = lazy(() => import('@/pages/Config/ShiftConfig'));
const Login = lazy(() => import('@/pages/Login'));
const UserList = lazy(() => import('@/pages/System/User'));
const RoleList = lazy(() => import('@/pages/System/Role'));
const OrgManagement = lazy(() => import('@/pages/System/Org'));
const EntityManagement = lazy(() => import('@/pages/System/Entity'));
const MenuList = lazy(() => import('@/pages/System/Menu'));
const TenantList = lazy(() => import('@/pages/System/Tenant'));
const OpenAppList = lazy(() => import('@/pages/System/OpenPlatform/index'));
const RCMDashboard = lazy(() => import('@/pages/RCM'));
const TenantPlans = lazy(() => import('@/pages/System/Billing/views/TenantPlansView'));
// const RoleRecharge = lazy(() => import('@/pages/System/Billing/views/RoleRechargeView')); // 旧版角色充值，已废弃
const EntityRecharge = lazy(() => import('@/pages/System/Billing/views/EntityRechargeView'));
const EntityPlans = lazy(() => import('@/pages/System/Billing/views/EntityPlansView'));
// 计费 v3.0 - 免费额度追踪
const UsageSummary = lazy(() => import('@/pages/System/Billing/views/UsageSummaryView'));
const ConsumeRecords = lazy(() => import('@/pages/System/Billing/views/ConsumeRecordsView'));
const LLMRoutes = lazy(() => import('@/pages/System/LLMRoutes'));

// 包装懒加载组件
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/login',
    element: withSuspense(Login),
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <MainLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: withSuspense(Home) },
      { path: 'workbench', element: withSuspense(Workbench) },
      // 评估管理 - 统一在MainLayout内展示
      { path: 'assessment/upload', element: withSuspense(AssessmentUpload) },
      { path: 'assessment/history', element: withSuspense(AssessmentHistory) },
      { path: 'schedule/generate', element: withSuspense(ScheduleGenerate) },
      { path: 'schedule/group', element: withSuspense(GroupSchedule) },
      { path: 'schedule/roster', element: withSuspense(RosterManagement) },
      { path: 'schedule/timeline-pdf', element: withSuspense(TimelinePdfExport) },
      { path: 'schedule/adjust', element: withSuspense(CareAdjust) },
      { path: 'caregiver', element: withSuspense(CaregiverList) },
      { path: 'caregiver/list', element: withSuspense(CaregiverList) },
      { path: 'group', element: withSuspense(GroupList) },
      { path: 'caregiver/elders', element: withSuspense(ElderList) },
      { path: 'elder/management', element: withSuspense(CareElderManagement) },
      { path: 'caregiver/skills-list', element: withSuspense(SkillList) },
      { path: 'caregiver/skills', element: withSuspense(CaregiverSkills) },
      { path: 'data-generator/elder', element: withSuspense(ElderNeeds) },
      { path: 'data-generator/scenario', element: withSuspense(MatchingScenario) },
      { path: 'dashboard', element: withSuspense(Dashboard) },
      { path: 'dashboard/national', element: withSuspense(NationalDashboard) },
      { path: 'rcm', element: withSuspense(RCMDashboard) },
      { path: 'timeline', element: withSuspense(Timeline) },
      { path: 'ocr', element: withSuspense(OCR) },
      { path: 'sop/import', element: withSuspense(SOPImport) },
      { path: 'sop/management', element: withSuspense(SOPManagement) },
      { path: 'sop/nursing-actions', element: withSuspense(NursingActions) },
      { path: 'config/shift', element: withSuspense(ShiftConfig) },
      { path: 'system/user', element: withSuspense(UserList) },
      { path: 'system/role', element: withSuspense(RoleList) },
      { path: 'system/org', element: withSuspense(OrgManagement) },
      { path: 'system/entity', element: withSuspense(EntityManagement) },
      { path: 'system/menu', element: withSuspense(MenuList) },
      { path: 'system/tenant', element: withSuspense(TenantList) },
      { path: 'system/open-platform', element: withSuspense(OpenAppList) },
      { path: 'system/billing', element: <Navigate to="/system/billing/plans" replace /> },
      { path: 'system/billing/plans', element: withSuspense(TenantPlans) },
      { path: 'system/billing/entity-recharge', element: withSuspense(EntityRecharge) },
      { path: 'system/billing/entity-plans', element: withSuspense(EntityPlans) },
      // 计费管理（新路径）
      {
        path: 'system/entity-billing',
        element: <Navigate to="/system/entity-billing/entity-recharge" replace />,
      },
      { path: 'system/entity-billing/entity-recharge', element: withSuspense(EntityRecharge) },
      // 计费 v3.0 - 免费额度追踪
      { path: 'system/billing/usage-summary', element: withSuspense(UsageSummary) },
      { path: 'system/billing/consume-records', element: withSuspense(ConsumeRecords) },
      { path: 'system/llm-routes', element: withSuspense(LLMRoutes) },
    ],
  },
]);

export default router;
